import React, { createContext, useState, useEffect } from 'react';
import { supabase } from '../lib/supabase';

export const AuthContext = createContext(null);

// Limpa todos os tokens do Supabase do localStorage
function clearSupabaseStorage() {
  try {
    const keys = Object.keys(localStorage);
    for (const key of keys) {
      if (key.startsWith('sb-')) {
        localStorage.removeItem(key);
      }
    }
  } catch {
    // noop
  }
}

export function AuthProvider({ children }) {
  const [session, setSession] = useState(null);
  const [profile, setProfile] = useState(null);
  const [loading, setLoading] = useState(true);

  const fetchProfile = async (userId) => {
    try {
      const { data, error } = await supabase
        .from('agency_profiles')
        .select('*')
        .eq('id', userId)
        .single();

      if (error) {
        console.warn('Perfil nao encontrado:', error.message);
        return null;
      }
      return data;
    } catch (err) {
      console.warn('Erro ao buscar perfil:', err);
      return null;
    }
  };

  // Valida se a sessao esta funcional fazendo um request real
  const validateSession = async (s) => {
    if (!s?.user?.id) return false;
    try {
      const { error } = await supabase
        .from('agency_profiles')
        .select('id')
        .eq('id', s.user.id)
        .single();
      return !error;
    } catch {
      return false;
    }
  };

  useEffect(() => {
    let mounted = true;
    let initialized = false;

    const initialize = async () => {
      try {
        const { data: { session: s }, error } = await supabase.auth.getSession();

        if (!mounted || initialized) return;
        initialized = true;

        if (error || !s) {
          setSession(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        // Validar se o token realmente funciona
        const isValid = await validateSession(s);
        if (!mounted) return;

        if (!isValid) {
          // Token expirado/invalido — limpar e ir pro login
          clearSupabaseStorage();
          setSession(null);
          setProfile(null);
          setLoading(false);
          return;
        }

        setSession(s);
        const p = await fetchProfile(s.user.id);
        if (mounted) {
          setProfile(p);
          setLoading(false);
        }
      } catch {
        if (mounted && !initialized) {
          initialized = true;
          clearSupabaseStorage();
          setSession(null);
          setProfile(null);
          setLoading(false);
        }
      }
    };

    // Timeout de seguranca — se tudo travar, limpa e vai pro login
    const safetyTimeout = setTimeout(() => {
      if (mounted && !initialized) {
        initialized = true;
        console.warn('Auth timeout — limpando sessao');
        clearSupabaseStorage();
        setSession(null);
        setProfile(null);
        setLoading(false);
      }
    }, 8000);

    initialize();

    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      async (event, s) => {
        if (!mounted) return;

        if (event === 'SIGNED_OUT' || event === 'USER_DELETED') {
          setSession(null);
          setProfile(null);
          return;
        }

        if (event === 'SIGNED_IN' || event === 'TOKEN_REFRESHED') {
          setSession(s);
          if (s?.user) {
            const p = await fetchProfile(s.user.id);
            if (mounted) setProfile(p);
          }
          if (mounted) setLoading(false);
        }
      }
    );

    return () => {
      mounted = false;
      clearTimeout(safetyTimeout);
      subscription.unsubscribe();
    };
  }, []);

  const signIn = async (email, password) => {
    const { data, error } = await supabase.auth.signInWithPassword({ email, password });
    if (error) throw error;
    return data;
  };

  const signOut = async () => {
    try {
      await supabase.auth.signOut();
    } catch {
      // noop
    }
    clearSupabaseStorage();
    setSession(null);
    setProfile(null);
  };

  const value = {
    session,
    user: session?.user ?? null,
    profile,
    loading,
    signIn,
    signOut,
    refetchProfile: async () => {
      if (session?.user) {
        const p = await fetchProfile(session.user.id);
        setProfile(p);
      }
    },
  };

  return (
    <AuthContext.Provider value={value}>
      {children}
    </AuthContext.Provider>
  );
}
